import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";

import { ListFooter } from "components";

import { ListFilterBase, Permission, ScholarshipStatusListDto, ScholarshipStatusListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipStatusListPageHeader } from "../ScholarshipStatusListPage/ScholarshipStatusListPageHeader"
import { ScholarshipStatusTable } from "./ScholarshipStatusTable";

const routeName = "scholarshipStatus";

export const scholarshipStatusListPageRoute: PageRoute = {
    handle: "Scholarship Status",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const ScholarshipStatusListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;

    const [listItems, setListItems] = useState<ScholarshipStatusListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<ScholarshipStatusListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.scholarshipStatus.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.scholarshipStatus.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.scholarshipStatus.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<ScholarshipStatusListPageHeader rootPermission={ROOT_PERMISSION} handle={scholarshipStatusListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <ScholarshipStatusTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            scholarshipStatus={listItems}
                            setScholarshipStatus={setListItems} 
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};
